import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowBackIcon,
  ArrowUpDownIcon,
  CheckIcon,
  CloseIcon,
  DownloadIcon,
  EditIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useBreakpointValue,
  Button,
  IconButton,
  Select,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  convertDocument,
  getTranslateLanguages,
  type ConvertDocumentHtmlPayload,
  type TranslateLanguage,
} from "../libs/qazbilim-backend";
import { DocViewer } from "./documents";

const ALLOWED_TYPES =
  "application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.json,.xml,.html,.htm,application/zip";

const buildEditableTranslationSrcDoc = (content: string) => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: #fff;
      }
      div[id^="page"] {
        position: relative !important;
        margin: 0 auto 12px auto;
        background: #fff;
        overflow: hidden;
      }
      div[id^="page"] p {
        position: absolute;
        margin: 0;
        white-space: pre;
      }
      div[id^="page"] span {
        white-space: pre;
      }
      div[id^="page"] img {
        position: absolute;
      }
    </style>
  </head>
  <body contenteditable="true">${content}</body>
</html>`;

const ProcessDocument = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const textColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const expandedPaneBg = useColorModeValue("white", "gray.800");
  const fullscreenBtnBg = useColorModeValue("gray.800", "gray.900");
  const fullscreenBtnHoverBg = useColorModeValue("black", "gray.700");
  const showOutputMenuText =
    useBreakpointValue({ base: false, lg: true }) ?? false;

  const [languages, setLanguages] = useState<TranslateLanguage[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(true);
  const [targetLang, setTargetLang] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [convertedOutput, setConvertedOutput] = useState<string | null>(null);
  const [translatedHtml, setTranslatedHtml] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isEditingTranslation, setIsEditingTranslation] = useState(false);
  const [translationDraft, setTranslationDraft] = useState("");
  const [previewThumbnailsExpanded, setPreviewThumbnailsExpanded] =
    useState(true);
  const [translationThumbnailsExpanded, setTranslationThumbnailsExpanded] =
    useState(true);
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== "undefined" ? window.innerHeight : 900,
  );
  const [expandedPane, setExpandedPane] = useState<
    "preview" | "translation" | null
  >(null);
  const translationEditorFrameRef = useRef<HTMLIFrameElement | null>(null);

  const TEXT_PREVIEW_TYPES = [
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/json",
    "text/xml",
    "application/xml",
    "text/html",
  ];
  const TEXT_EXTENSIONS = [
    ".txt",
    ".md",
    ".csv",
    ".json",
    ".xml",
    ".html",
    ".htm",
  ];

  const isTextFile = (f: File) =>
    TEXT_PREVIEW_TYPES.some((t) => f.type === t) ||
    TEXT_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext));

  useEffect(() => {
    let cancelled = false;
    getTranslateLanguages()
      .then((list) => {
        if (!cancelled) {
          setLanguages(list);
          if (list.length > 0) {
            setTargetLang((prev) => prev || list[0].code);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load languages",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLanguagesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!file) {
      setTextPreview(null);
      setPreviewLoading(false);
      return;
    }
    if (file.type === "application/pdf") {
      setTextPreview(null);
      setPreviewLoading(false);
      return;
    }
    if (isTextFile(file)) {
      setPreviewLoading(true);
      let cancelled = false;
      const reader = new FileReader();
      reader.onload = () => {
        if (!cancelled) {
          setTextPreview((reader.result as string) ?? null);
          setPreviewLoading(false);
        }
      };
      reader.onerror = () => {
        if (!cancelled) {
          setTextPreview(null);
          setPreviewLoading(false);
        }
      };
      reader.readAsText(file.slice(0, 1024 * 100)); // first 100KB for preview
      return () => {
        cancelled = true;
        setTextPreview(null);
        setPreviewLoading(false);
      };
    }
    setTextPreview(null);
    setPreviewLoading(false);
  }, [file]);

  useEffect(() => {
    const updateHeight = () => setViewportHeight(window.innerHeight);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const onFileChange = useCallback((selected: File | null) => {
    setFile(selected);
    setError(null);
    setConvertedOutput(null);
    setTranslatedHtml(null);
    setIsEditingTranslation(false);
    setTranslationDraft("");
    setPreviewThumbnailsExpanded(true);
    setTranslationThumbnailsExpanded(true);
    setExpandedPane(null);
    setTextPreview(null);
  }, []);

  const handleUploadNew = useCallback(() => {
    setFile(null);
    setError(null);
    setConvertedOutput(null);
    setTranslatedHtml(null);
    setIsEditingTranslation(false);
    setTranslationDraft("");
    setPreviewThumbnailsExpanded(true);
    setTranslationThumbnailsExpanded(true);
    setExpandedPane(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const f = e.dataTransfer.files?.[0];
      if (f) onFileChange(f);
    },
    [onFileChange],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleProcess = useCallback(async () => {
    if (!file || !targetLang) {
      toast({
        title: t("document.errorMissing", "Select a file and target language"),
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setError(null);
    setConvertedOutput(null);
    setTranslatedHtml(null);
    setIsEditingTranslation(false);
    setTranslationDraft("");
    setProcessing(true);
    try {
      const isPdf = file.type === "application/pdf";
      const output = await convertDocument(file, {
        output_type: isPdf ? "html" : "markdown",
        target_lang: isPdf ? targetLang : undefined,
      });
      if (isPdf) {
        if (typeof output === "string") {
          setTranslatedHtml(output);
          setTranslationDraft(output);
        } else {
          const payload = output as ConvertDocumentHtmlPayload;
          const translated =
            payload.translated_html ?? payload.pre_translate_html ?? "";
          setTranslatedHtml(translated);
          setTranslationDraft(translated);
        }
      } else {
        const converted = typeof output === "string" ? output : "";
        setConvertedOutput(converted);
        setTranslationDraft(converted);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Conversion failed";
      setError(message);
      toast({
        title: t("document.errorProcess", "Conversion failed"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  }, [file, targetLang, t, toast]);

  const hasResults = convertedOutput != null || translatedHtml != null;
  const selectedLanguageName =
    languages.find((lang) => lang.code === targetLang)?.name ?? targetLang;
  const textPreviewSx = {
    "& pre": { margin: 0 },
  };
  const isPreviewExpanded = expandedPane === "preview";
  const isTranslationExpanded = expandedPane === "translation";
  const isAnyPaneExpanded = expandedPane != null;
  const canToggleTranslationThumbnails = file?.type === "application/pdf";
  const expandedViewerHeight = Math.max(420, viewportHeight - 220);
  const translationContent =
    file?.type === "application/pdf"
      ? (translatedHtml ?? "")
      : (convertedOutput ?? "");

  useEffect(() => {
    if (!isAnyPaneExpanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAnyPaneExpanded]);

  const handleDownloadTranslation = useCallback(async () => {
    if (!translationContent) return;

    const originalExt = (file?.name.split(".").pop() || "").toLowerCase();
    const baseName = (file?.name || "translation").replace(/\.[^.]+$/, "");

    // For PDF uploads, export edited/translated content back as PDF.
    if (originalExt === "pdf") {
      const htmlForPdf = translationContent.trim().startsWith("<")
        ? translationContent
        : `<pre>${translationContent
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</pre>`;

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "0";
      container.style.top = "0";
      container.style.width = "794px";
      container.style.minHeight = "1px";
      container.style.zIndex = "-1";
      container.style.pointerEvents = "none";
      container.style.background = "white";
      container.style.opacity = "1";
      container.innerHTML = `
        <style>
          html, body { margin: 0; padding: 0; background: #fff; color: #000; }
          #pdf-export-root div[id^="page"] {
            position: relative !important;
            margin: 0 auto 12px auto;
            background: #fff;
            overflow: hidden;
          }
          #pdf-export-root div[id^="page"] p {
            position: absolute;
            margin: 0;
            white-space: pre;
            color: #000 !important;
          }
          #pdf-export-root div[id^="page"] span {
            white-space: pre;
            color: #000 !important;
          }
          #pdf-export-root div[id^="page"] img {
            position: absolute;
          }
        </style>
        <div id="pdf-export-root">${htmlForPdf}</div>
      `;
      document.body.appendChild(container);

      try {
        const images = Array.from(container.querySelectorAll("img"));
        await Promise.all(
          images.map(
            (img) =>
              new Promise<void>((resolve) => {
                if (img.complete) {
                  resolve();
                  return;
                }
                img.addEventListener("load", () => resolve(), { once: true });
                img.addEventListener("error", () => resolve(), { once: true });
              }),
          ),
        );
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        const exportRoot =
          container.querySelector<HTMLElement>("#pdf-export-root") ?? container;
        const html2pdf = (await import("html2pdf.js")).default as any;
        const pdfBlob = await html2pdf()
          .set({
            margin: 0,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              scrollX: 0,
              scrollY: 0,
              windowWidth: 794,
            },
            jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
          })
          .from(exportRoot)
          .toPdf()
          .outputPdf("blob");

        // Very small PDFs are typically blank in this flow -> fallback to jsPDF text export.
        if (pdfBlob.size < 3000) {
          const { jsPDF } = await import("jspdf");
          const parser = new DOMParser();
          const parsed = parser.parseFromString(htmlForPdf, "text/html");
          const text = (parsed.body.textContent || translationContent || "")
            .replace(/\r\n/g, "\n")
            .replace(/\u00a0/g, " ")
            .trim();
          const fallbackDoc = new jsPDF({
            unit: "pt",
            format: "a4",
            orientation: "portrait",
          });
          const pageWidth = fallbackDoc.internal.pageSize.getWidth();
          const pageHeight = fallbackDoc.internal.pageSize.getHeight();
          const margin = 40;
          const lineHeight = 16;
          const lines = fallbackDoc.splitTextToSize(
            text || t("document.translation", "Translation"),
            pageWidth - margin * 2,
          ) as string[];
          let y = margin;
          lines.forEach((line, index) => {
            if (index > 0 && y + lineHeight > pageHeight - margin) {
              fallbackDoc.addPage();
              y = margin;
            }
            fallbackDoc.text(line, margin, y);
            y += lineHeight;
          });
          const fallbackBlob = fallbackDoc.output("blob");
          const fallbackUrl = URL.createObjectURL(fallbackBlob);
          const fallbackAnchor = document.createElement("a");
          fallbackAnchor.href = fallbackUrl;
          fallbackAnchor.download = `${baseName}-translation.pdf`;
          document.body.appendChild(fallbackAnchor);
          fallbackAnchor.click();
          fallbackAnchor.remove();
          URL.revokeObjectURL(fallbackUrl);
        } else {
          const url = URL.createObjectURL(pdfBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${baseName}-translation.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }
      } finally {
        container.remove();
      }
      return;
    }

    const ext =
      originalExt ||
      (translationContent.trim().startsWith("<") ? "html" : "txt");
    const mimeMap: Record<string, string> = {
      html: "text/html;charset=utf-8",
      htm: "text/html;charset=utf-8",
      txt: "text/plain;charset=utf-8",
      md: "text/markdown;charset=utf-8",
      csv: "text/csv;charset=utf-8",
      json: "application/json;charset=utf-8",
      xml: "application/xml;charset=utf-8",
    };
    const blob = new Blob([translationContent], {
      type: mimeMap[ext] || "application/octet-stream",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}-translation.${ext || "txt"}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [file?.name, translationContent]);

  return (
    <Container id="process-document-page" maxW="container.xl" py={8}>
      <VStack id="process-document-stack" spacing={6} align="stretch">
        {!hasResults && (
          <VStack
            id="process-document-input"
            spacing={6}
            align="stretch"
            w="100%"
          >
            <Heading id="process-document-title" as="h1" size="xl">
              {t("document.title", "Document")}
            </Heading>
            <Text id="process-document-description" color={textColor}>
              {t(
                "document.description",
                "Upload a document to extract text and translate it.",
              )}
            </Text>

            <Box
              id="process-document-upload-zone"
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={isDragging ? "blue.400" : borderColor}
              borderRadius="md"
              p={8}
              textAlign="center"
              bg={isDragging ? "blue.50" : "transparent"}
              _dark={{ bg: isDragging ? "blue.900" : "transparent" }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              _hover={{ bg: hoverBg }}
              transition="background 0.2s"
            >
              <input
                type="file"
                accept={ALLOWED_TYPES}
                style={{ display: "none" }}
                id="document-upload"
                aria-label={t(
                  "document.dropOrClick",
                  "Drop a file here or click to choose",
                )}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  onFileChange(f ?? null);
                  e.target.value = "";
                }}
              />
              <label
                id="process-document-upload-label"
                htmlFor="document-upload"
                style={{ cursor: "pointer", display: "block" }}
              >
                <VStack id="process-document-upload-label-stack" spacing={2}>
                  <Text
                    id="process-document-upload-drop-text"
                    color={textColor}
                  >
                    {t(
                      "document.dropOrClick",
                      "Drop a file here or click to choose",
                    )}
                  </Text>
                  <Text
                    id="process-document-upload-types-text"
                    fontSize="sm"
                    color={textColor}
                  >
                    {t(
                      "document.allowedTypes",
                      "PDF, Word, Excel, PowerPoint, TXT, MD, CSV, etc.",
                    )}
                  </Text>
                  {file && (
                    <Text
                      id="process-document-file-name"
                      fontWeight="medium"
                      noOfLines={1}
                    >
                      {file.name}
                    </Text>
                  )}
                </VStack>
              </label>
            </Box>

            {file && (
              <VStack
                id="process-document-preview"
                align="stretch"
                spacing={2}
                w="100%"
              >
                <Heading
                  id="process-document-preview-heading"
                  size="sm"
                  fontWeight="600"
                >
                  {t("document.preview", "Preview")}
                </Heading>
                <Box
                  id="process-document-preview-box"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="md"
                  overflow="hidden"
                  bg={cardBg}
                >
                  {file?.type === "application/pdf" && (
                    <DocViewer
                      file={file}
                      idPrefix="process-document-pdf"
                      maxHeight={420}
                    />
                  )}
                  {file?.type !== "application/pdf" && textPreview != null && (
                    <Box
                      id="process-document-preview-text-content"
                      maxH="320px"
                      overflowY="auto"
                      p={4}
                      fontSize="sm"
                      fontFamily="mono"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      sx={textPreviewSx}
                    >
                      {textPreview}
                    </Box>
                  )}
                  {file?.type !== "application/pdf" &&
                    textPreview == null &&
                    !previewLoading && (
                      <Box
                        id="process-document-preview-unavailable"
                        p={4}
                        color={mutedColor}
                        fontSize="sm"
                      >
                        {t(
                          "document.previewUnavailable",
                          "Preview not available for this file type.",
                        )}
                      </Box>
                    )}
                  {file?.type !== "application/pdf" && previewLoading && (
                    <Box
                      id="process-document-preview-text-loading"
                      p={4}
                      color={mutedColor}
                      fontSize="sm"
                    >
                      {t("document.previewLoading", "Loading preview…")}
                    </Box>
                  )}
                </Box>
              </VStack>
            )}

            <FormControl id="process-document-language-control">
              <FormLabel id="process-document-language-label">
                {t("document.translateTo", "Translate to")}
              </FormLabel>
              <Select
                id="process-document-language-select"
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                placeholder={
                  languagesLoading
                    ? t("document.loadingLanguages", "Loading...")
                    : undefined
                }
                isDisabled={languagesLoading}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name} ({lang.code})
                  </option>
                ))}
              </Select>
            </FormControl>

            <Button
              id="process-document-submit"
              colorScheme="blue"
              onClick={handleProcess}
              isLoading={processing}
              loadingText={t(
                "document.processing",
                "Converting and translating...",
              )}
              isDisabled={!file || !targetLang || languagesLoading}
            >
              {t("document.process", "Translate document")}
            </Button>

            {error && (
              <Alert
                id="process-document-error"
                status="error"
                borderRadius="md"
              >
                <AlertIcon id="process-document-error-icon" />
                <AlertTitle id="process-document-error-title">
                  {t("document.error", "Error")}
                </AlertTitle>
                <AlertDescription id="process-document-error-description">
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        )}

        {hasResults && (
          <VStack
            id="process-document-results"
            align="stretch"
            spacing={4}
            w="100%"
          >
            <Box
              id="process-document-results-panes"
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              gap={4}
              w="100%"
              alignItems="stretch"
            >
              {file?.type === "application/pdf" && (
                <VStack
                  id="process-document-results-preview-pane"
                  align="stretch"
                  spacing={2}
                  flex="1"
                  minW={0}
                  display={
                    isAnyPaneExpanded && !isPreviewExpanded ? "none" : "flex"
                  }
                  position={isPreviewExpanded ? "fixed" : "relative"}
                  top={
                    isPreviewExpanded ? { base: "72px", md: "84px" } : undefined
                  }
                  left={isPreviewExpanded ? { base: 3, md: 6 } : undefined}
                  right={isPreviewExpanded ? { base: 3, md: 6 } : undefined}
                  bottom={isPreviewExpanded ? { base: 3, md: 6 } : undefined}
                  zIndex={isPreviewExpanded ? 20 : undefined}
                  bg={isPreviewExpanded ? expandedPaneBg : undefined}
                  p={isPreviewExpanded ? 3 : undefined}
                  borderRadius={isPreviewExpanded ? "md" : undefined}
                  boxShadow={isPreviewExpanded ? "xl" : undefined}
                >
                  <Heading
                    id="process-document-results-preview-heading"
                    size="lg"
                    fontWeight="600"
                  >
                    {t("document.preview", "Preview")}
                  </Heading>
                  <Box
                    id="process-document-results-preview-box"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    borderRadius="md"
                    overflow="hidden"
                    bg={cardBg}
                    flex="1"
                    minH={0}
                  >
                    <DocViewer
                      file={file}
                      idPrefix="process-document-result-preview"
                      maxHeight={isPreviewExpanded ? expandedViewerHeight : 420}
                      mobileThumbnailsExpanded={previewThumbnailsExpanded}
                      onMobileThumbnailsExpandedChange={
                        setPreviewThumbnailsExpanded
                      }
                      hideInlineThumbnailToggle
                    />
                  </Box>
                  <HStack spacing={2}>
                    <IconButton
                      id="process-document-results-preview-pages-button"
                      isRound
                      variant="outline"
                      size="md"
                      aria-label={
                        previewThumbnailsExpanded
                          ? t("document.hidePages", "Hide pages")
                          : t("document.showPages", "Show pages")
                      }
                      icon={<HamburgerIcon boxSize={4} />}
                      onClick={() =>
                        setPreviewThumbnailsExpanded((prev) => !prev)
                      }
                    />
                    <Button
                      id="process-document-results-preview-expand-button"
                      leftIcon={<ArrowUpDownIcon />}
                      flex="1"
                      colorScheme="blackAlpha"
                      bg={fullscreenBtnBg}
                      color="white"
                      _hover={{ bg: fullscreenBtnHoverBg }}
                      variant="solid"
                      boxShadow="md"
                      py={6}
                      onClick={() =>
                        setExpandedPane((prev) =>
                          prev === "preview" ? null : "preview",
                        )
                      }
                    >
                      {isPreviewExpanded
                        ? t("document.collapseView", "Collapse view")
                        : t("document.expandView", "Expand view")}
                    </Button>
                  </HStack>
                </VStack>
              )}
              <VStack
                id="process-document-results-output-pane"
                align="stretch"
                spacing={2}
                flex="1"
                minW={0}
                display={
                  isAnyPaneExpanded && !isTranslationExpanded ? "none" : "flex"
                }
                position={isTranslationExpanded ? "fixed" : "relative"}
                top={
                  isTranslationExpanded
                    ? { base: "72px", md: "84px" }
                    : undefined
                }
                left={isTranslationExpanded ? { base: 3, md: 6 } : undefined}
                right={isTranslationExpanded ? { base: 3, md: 6 } : undefined}
                bottom={isTranslationExpanded ? { base: 3, md: 6 } : undefined}
                zIndex={isTranslationExpanded ? 20 : undefined}
                bg={isTranslationExpanded ? expandedPaneBg : undefined}
                p={isTranslationExpanded ? 3 : undefined}
                borderRadius={isTranslationExpanded ? "md" : undefined}
                boxShadow={isTranslationExpanded ? "xl" : undefined}
              >
                <Heading
                  id="process-document-results-output-heading"
                  size="lg"
                  fontWeight="600"
                >
                  {t("document.translation", "Translation")}
                </Heading>
                {isTranslationExpanded && (
                  <HStack
                    id="process-document-results-output-menu-bar"
                    spacing={2}
                    justify="flex-end"
                    flexWrap="nowrap"
                    overflowX="auto"
                    overflowY="hidden"
                    whiteSpace="nowrap"
                    sx={{ "& > *": { flexShrink: 0 } }}
                  >
                    {!isEditingTranslation ? (
                      showOutputMenuText ? (
                        <Button
                          id="process-document-results-output-edit-button"
                          variant="outline"
                          size="md"
                          leftIcon={<EditIcon boxSize={4} />}
                          onClick={() => {
                            setTranslationDraft(translationContent);
                            setIsEditingTranslation(true);
                          }}
                        >
                          {t("document.menuEdit", "Edit")}
                        </Button>
                      ) : (
                        <IconButton
                          id="process-document-results-output-edit-button"
                          isRound
                          variant="outline"
                          size="md"
                          aria-label={t(
                            "document.editTranslation",
                            "Edit translation",
                          )}
                          icon={<EditIcon boxSize={4} />}
                          onClick={() => {
                            setTranslationDraft(translationContent);
                            setIsEditingTranslation(true);
                          }}
                        />
                      )
                    ) : (
                      <>
                        {showOutputMenuText ? (
                          <Button
                            id="process-document-results-output-save-button"
                            colorScheme="blue"
                            size="md"
                            leftIcon={<CheckIcon boxSize={4} />}
                            onClick={() => {
                              const edited =
                                translationEditorFrameRef.current
                                  ?.contentDocument?.body?.innerHTML ??
                                translationDraft;
                              if (file?.type === "application/pdf")
                                setTranslatedHtml(edited);
                              else setConvertedOutput(edited);
                              setTranslationDraft(edited);
                              setIsEditingTranslation(false);
                            }}
                          >
                            {t("document.menuSave", "Save")}
                          </Button>
                        ) : (
                          <IconButton
                            id="process-document-results-output-save-button"
                            isRound
                            colorScheme="blue"
                            size="md"
                            aria-label={t("document.saveTranslation", "Save")}
                            icon={<CheckIcon boxSize={4} />}
                            onClick={() => {
                              const edited =
                                translationEditorFrameRef.current
                                  ?.contentDocument?.body?.innerHTML ??
                                translationDraft;
                              if (file?.type === "application/pdf")
                                setTranslatedHtml(edited);
                              else setConvertedOutput(edited);
                              setTranslationDraft(edited);
                              setIsEditingTranslation(false);
                            }}
                          />
                        )}
                        {showOutputMenuText ? (
                          <Button
                            id="process-document-results-output-cancel-button"
                            variant="outline"
                            size="md"
                            leftIcon={<CloseIcon boxSize={4} />}
                            onClick={() => {
                              setTranslationDraft(translationContent);
                              setIsEditingTranslation(false);
                            }}
                          >
                            {t("document.menuCancel", "Cancel")}
                          </Button>
                        ) : (
                          <IconButton
                            id="process-document-results-output-cancel-button"
                            isRound
                            variant="outline"
                            size="md"
                            aria-label={t("document.cancelEdit", "Cancel")}
                            icon={<CloseIcon boxSize={4} />}
                            onClick={() => {
                              setTranslationDraft(translationContent);
                              setIsEditingTranslation(false);
                            }}
                          />
                        )}
                      </>
                    )}
                    {canToggleTranslationThumbnails &&
                      (showOutputMenuText ? (
                        <Button
                          id="process-document-results-output-pages-button"
                          variant="outline"
                          size="md"
                          leftIcon={<HamburgerIcon boxSize={4} />}
                          onClick={() =>
                            setTranslationThumbnailsExpanded((prev) => !prev)
                          }
                        >
                          {translationThumbnailsExpanded
                            ? t("document.hidePages", "Hide pages")
                            : t("document.showPages", "Show pages")}
                        </Button>
                      ) : (
                        <IconButton
                          id="process-document-results-output-pages-button"
                          isRound
                          variant="outline"
                          size="md"
                          aria-label={
                            translationThumbnailsExpanded
                              ? t("document.hidePages", "Hide pages")
                              : t("document.showPages", "Show pages")
                          }
                          icon={<HamburgerIcon boxSize={4} />}
                          onClick={() =>
                            setTranslationThumbnailsExpanded((prev) => !prev)
                          }
                        />
                      ))}
                    {showOutputMenuText ? (
                      <Button
                        id="process-document-results-output-download-button"
                        variant="outline"
                        size="md"
                        leftIcon={<DownloadIcon boxSize={4} />}
                        onClick={handleDownloadTranslation}
                        isDisabled={!translationContent}
                      >
                        {t("document.menuDownload", "Download")}
                      </Button>
                    ) : (
                      <IconButton
                        id="process-document-results-output-download-button"
                        isRound
                        variant="outline"
                        size="md"
                        aria-label={t("document.download", "Download")}
                        icon={<DownloadIcon boxSize={4} />}
                        onClick={handleDownloadTranslation}
                        isDisabled={!translationContent}
                      />
                    )}
                    {showOutputMenuText ? (
                      <Button
                        id="process-document-results-output-collapse-button"
                        variant="outline"
                        size="md"
                        leftIcon={<ArrowBackIcon boxSize={4} />}
                        onClick={() => setExpandedPane(null)}
                      >
                        {t("document.menuCollapse", "Collapse")}
                      </Button>
                    ) : (
                      <IconButton
                        id="process-document-results-output-collapse-button"
                        isRound
                        variant="outline"
                        size="md"
                        aria-label={t("document.collapseView", "Collapse view")}
                        icon={<ArrowBackIcon boxSize={4} />}
                        onClick={() => setExpandedPane(null)}
                      />
                    )}
                  </HStack>
                )}
                <Box
                  id="process-document-results-output-box"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="md"
                  overflow="hidden"
                  bg={cardBg}
                  flex="1"
                  minH={0}
                >
                  {isEditingTranslation ? (
                    <Box
                      width="100%"
                      height={
                        isTranslationExpanded
                          ? `${expandedViewerHeight}px`
                          : "420px"
                      }
                    >
                      <iframe
                        id="process-document-results-output-editor"
                        ref={translationEditorFrameRef}
                        title="translation-editor-frame"
                        srcDoc={buildEditableTranslationSrcDoc(
                          translationDraft,
                        )}
                        sandbox="allow-same-origin"
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          background: "white",
                        }}
                      />
                    </Box>
                  ) : (
                    <DocViewer
                      result={{
                        extractedText: null,
                        translatedText: translationContent,
                        translatedLanguageName: selectedLanguageName,
                      }}
                      idPrefix="process-document-result"
                      maxHeight={
                        isTranslationExpanded ? expandedViewerHeight : 420
                      }
                      mobileThumbnailsExpanded={translationThumbnailsExpanded}
                      onMobileThumbnailsExpandedChange={
                        setTranslationThumbnailsExpanded
                      }
                      hideInlineThumbnailToggle
                    />
                  )}
                </Box>
                {!isTranslationExpanded && (
                  <HStack spacing={2}>
                    {canToggleTranslationThumbnails && (
                      <IconButton
                        id="process-document-results-output-pages-inline-button"
                        isRound
                        variant="outline"
                        size="md"
                        aria-label={
                          translationThumbnailsExpanded
                            ? t("document.hidePages", "Hide pages")
                            : t("document.showPages", "Show pages")
                        }
                        icon={<HamburgerIcon boxSize={4} />}
                        onClick={() =>
                          setTranslationThumbnailsExpanded((prev) => !prev)
                        }
                      />
                    )}
                    <Button
                      id="process-document-results-output-expand-button"
                      leftIcon={<ArrowUpDownIcon />}
                      flex="1"
                      colorScheme="blackAlpha"
                      bg={fullscreenBtnBg}
                      color="white"
                      _hover={{ bg: fullscreenBtnHoverBg }}
                      variant="solid"
                      boxShadow="md"
                      py={6}
                      onClick={() => setExpandedPane("translation")}
                    >
                      {t("document.expandView", "Expand view")}
                    </Button>
                  </HStack>
                )}
              </VStack>
            </Box>
            <Button
              id="process-document-upload-new"
              variant="outline"
              colorScheme="blue"
              onClick={handleUploadNew}
            >
              {t("document.uploadNew", "Upload new document")}
            </Button>
          </VStack>
        )}
      </VStack>
    </Container>
  );
};

export default ProcessDocument;
